import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/SiteHeader";
import { getWelcomeState, markWelcomeSeen } from "@/lib/welcome.functions";

const LESSON_1_1_PATH =
  "/courses/business-and-accountancy/basic-business-accountancy/lessons/1.1";

const MONO = '"JetBrains Mono", monospace';
const SERIF = '"Cormorant Garamond", serif';
const INK = "#1A1612";
const MUTED = "#5A534A";

export const Route = createFileRoute("/welcome")({
  head: () => ({
    meta: [
      { title: "Welcome — WHO KNEW" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: WelcomePage,
});

function WelcomePage() {
  const navigate = useNavigate();
  const fetchState = useServerFn(getWelcomeState);
  const markSeen = useServerFn(markWelcomeSeen);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!data.user) {
        navigate({ to: "/signin", replace: true });
        return;
      }
      try {
        const res = await fetchState();
        if (cancelled) return;
        if (res.welcomedAt) {
          window.location.assign(LESSON_1_1_PATH);
          return;
        }
        setReady(true);
      } catch {
        if (cancelled) return;
        setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, fetchState]);

  const dismiss = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await markSeen({});
    } catch {
      // non-blocking
    }
    window.location.assign(LESSON_1_1_PATH);
  };

  if (!ready) {
    return (
      <div className="min-h-screen bg-paper text-ink">
        <SiteHeader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper text-ink">
      <SiteHeader />
      <main className="flex justify-center px-6 md:px-8">
        <div
          className="w-full"
          style={{
            maxWidth: 680,
            paddingTop: 48,
            paddingBottom: 48,
          }}
        >
          <div className="md:pt-[80px] md:pb-[80px]">
            <p
              className="font-bold uppercase tracking-[0.05em] text-burgundy"
              style={{ fontFamily: MONO, fontSize: 11 }}
            >
              THE COURSE IS YOURS
            </p>
            <h1
              className="text-[40px] md:text-[56px] font-bold leading-[1.1] text-ink"
              style={{ marginTop: 24 }}
            >
              Welcome.
            </h1>
            <p
              style={{
                fontFamily: SERIF,
                fontSize: 16,
                lineHeight: 1.6,
                color: INK,
                marginTop: 32,
              }}
            >
              You have lifetime access to all 29 lessons, six workbooks, and six
              Excel templates. Your account is active, you've been signed in,
              and your receipt is on its way to your inbox.
            </p>
            <p
              style={{
                fontFamily: SERIF,
                fontSize: 16,
                lineHeight: 1.6,
                color: INK,
                marginTop: 16,
              }}
            >
              The course is short by design. Most lessons are under five
              minutes. Most students finish in a Saturday afternoon. No
              pressure — you can take it slow.
            </p>

            <button
              type="button"
              onClick={dismiss}
              disabled={busy}
              className="interactive-lift mt-10 block w-full md:w-auto md:inline-block bg-burgundy px-8 py-[18px] text-[14px] font-bold uppercase tracking-[0.05em] text-paper transition-colors hover:bg-[#6B1717] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: MONO }}
            >
              {busy ? "OPENING…" : "START LESSON 1.1"}
            </button>

            <p
              className="italic"
              style={{
                fontFamily: SERIF,
                fontSize: 14,
                color: MUTED,
                marginTop: 24,
              }}
            >
              Or use the sidebar to{" "}
              <button
                type="button"
                onClick={dismiss}
                disabled={busy}
                className="italic text-burgundy underline decoration-burgundy/40 decoration-1 underline-offset-4 hover:decoration-2 bg-transparent cursor-pointer p-0"
                style={{ fontFamily: SERIF, fontSize: 14, color: MUTED }}
              >
                <span style={{ color: MUTED }}>jump around</span>
              </button>
              . We're not the boss of you.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
